import { z } from 'zod';
import { uuidSchema, countryCodeSchema, isoDateSchema } from './common';

/**
 * 🇪🇺 PSD2 Open Banking — Enable Banking (https://enablebanking.com) backed.
 *
 * MVP supports the EU/UK markets where Enable Banking has PSD2 coverage:
 * DE, AT, NL, FR, BE, IE, GB, FI and many more.
 */

export const bankConnectionStatusSchema = z.enum([
  'pending', // Requisition created, kullanıcı henüz consent vermedi
  'active', // Consent verildi, hesaplar erişilebilir
  'expired', // 90 günlük PSD2 consent süresi doldu
  'revoked', // Kullanıcı veya banka iptal etti
  'error', // API hatası
]);
export type BankConnectionStatus = z.infer<typeof bankConnectionStatusSchema>;

export const listInstitutionsQuerySchema = z.object({
  country: countryCodeSchema,
});
export type ListInstitutionsQuery = z.infer<typeof listInstitutionsQuerySchema>;

export const connectBankSchema = z.object({
  institutionId: z.string().min(1).max(120),
  redirectUrl: z.string().url(),
});
export type ConnectBankInput = z.infer<typeof connectBankSchema>;

export const linkBankAccountSchema = z.object({
  externalAccountId: z.string().min(1).max(120),
  moneyAccountId: uuidSchema,
});
export type LinkBankAccountInput = z.infer<typeof linkBankAccountSchema>;

/**
 * 🔌 Bağlantı sağlayıcıları (provider). `enable-banking` PSD2 AISP akışı,
 * `wise`/`paypal` ise per-user OAuth "Connect" akışı kullanır. Hepsi ortak
 * `bank_connections` tablosu + ortak import/dedup motoru üzerinde çalışır.
 */
export const connectionProviderSchema = z.enum(['enable-banking', 'wise', 'paypal']);
export type ConnectionProvider = z.infer<typeof connectionProviderSchema>;

/** OAuth "Connect" başlatma — provider seçilir, redirect URL verilir. */
export const connectProviderSchema = z.object({
  provider: connectionProviderSchema,
  redirectUrl: z.string().url(),
});
export type ConnectProviderInput = z.infer<typeof connectProviderSchema>;

/** OAuth callback dönüşü — `?code=...&state=<conn.id>` → token exchange. */
export const finalizeProviderSchema = z.object({
  code: z.string().min(1).max(2000),
  state: z.string().optional(),
});
export type FinalizeProviderInput = z.infer<typeof finalizeProviderSchema>;

/**
 * Provider (Wise/PayPal) hesabını bağla. `moneyAccountId` opsiyonel: verilmezse
 * external hesaptan (ad + currency) otomatik bir `e_wallet` money account
 * oluşturulur — sürtünmesiz "tek tık" bağlama.
 */
export const linkProviderAccountSchema = z.object({
  externalAccountId: z.string().min(1).max(120),
  moneyAccountId: uuidSchema.optional(),
});
export type LinkProviderAccountInput = z.infer<typeof linkProviderAccountSchema>;

/**
 * İçe aktarılan bir işlemin mevcut elle girilmiş kayıtla ilişkisine kullanıcı
 * kararı: birleştir (merge), ayrı tut (keep) ya da iç transfer olarak işaretle
 * (transfer — ör. PayPal funding'i ↔ banka çekimi, çift-sayımı engeller).
 */
export const resolveMatchSchema = z.object({
  action: z.enum(['merge', 'keep', 'transfer']),
  /** merge seçilirse hangi mevcut transaction ile birleşileceği */
  matchTransactionId: uuidSchema.optional(),
});
export type ResolveMatchInput = z.infer<typeof resolveMatchSchema>;

/**
 * Bir provider bağlantısının "funding bankası"nı ayarla (PayPal'ın para çektiği
 * banka money account). null → bağı kaldır.
 */
export const setProviderFundingAccountSchema = z.object({
  moneyAccountId: uuidSchema.nullable(),
});
export type SetProviderFundingAccountInput = z.infer<typeof setProviderFundingAccountSchema>;

/** İçe aktarma modu: doğrudan ekle vs inceleme kutusuna düşür. */
export const importModeSchema = z.enum(['auto', 'review']);
export type ImportMode = z.infer<typeof importModeSchema>;

/** PayPal funding modeli: her harcamada settlement vs lump-sum yükleme transferi. */
export const fundingModeSchema = z.enum(['per_purchase', 'lump_sum']);
export type FundingMode = z.infer<typeof fundingModeSchema>;

/** Bağlantı ayarları (import/funding modu) güncelleme. */
export const connectionSettingsSchema = z.object({
  importMode: importModeSchema.optional(),
  fundingMode: fundingModeSchema.optional(),
});
export type ConnectionSettingsInput = z.infer<typeof connectionSettingsSchema>;

/**
 * İnceleme kutusundaki bir staging item'ın onaylanması — kullanıcının düzelttiği
 * alanlar. Hepsi opsiyonel; verilmeyenler için öneri/orijinal değer kullanılır.
 */
export const approveReviewItemSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  date: isoDateSchema.optional(),
  cardId: uuidSchema.optional(),
  categoryTitle: z.string().min(1).max(120).optional(),
  tagIds: z.array(uuidSchema).max(20).optional(),
  /** funding_transfer kind için: kullanıcının seçtiği funding banka hesabı. */
  fundingAccountId: uuidSchema.optional(),
});
export type ApproveReviewItemInput = z.infer<typeof approveReviewItemSchema>;

export const approveReviewBulkSchema = z.object({
  ids: z.array(uuidSchema).min(1).max(200),
});
export type ApproveReviewBulkInput = z.infer<typeof approveReviewBulkSchema>;

/**
 * POST /integrations/review/inbox/approve-bulk yanıtı. Olası kopya (`possibleDuplicate`)
 * işaretli kalemler toplu onayda ATLANIR (sunucu savunması) ve kutuda bekler; yalnız tek tek
 * (açık) onayla işlenir.
 */
export interface ApproveReviewBulkResult {
  approved: number;
  failed: number;
  /** possibleDuplicate işaretli olduğu için onaylanmayan (pending kalan) kalem sayısı. */
  skippedDuplicates: number;
}
